/**
 * 场景能力卡(2026-07-23 重构版):**人物 + 产物形制图示 + 三层文案**,真样图 hover 才浮出。
 *
 * ═══ 为什么从「大样图 + 文案」改成这样(全部实测,不是审美偏好)═══════════════════
 *  1. 同租户的真产物样图**长得都一样**(demo 租户全是橙汁杯),4 张卡的样图肉眼无法区分,
 *     却占了卡片 60% 面积 —— 零区分信息。
 *  2. 长图产物 `2160×9727` 裁成 4:3 只剩顶部一条,**看不出它是长图**;样图对长图族完全失效。
 *  3. 4 列时卡宽仅 252px,旧版人物占 43% → 描述只剩 10 字/行,20-30 字全被 clamp 截断。
 * 故:人物**完全收进舞台区**(不压文案),形制用几何图示常驻,真样图退到 hover。
 *
 * ★ 铁律没破:`catalog.ts:8` 锁的是「enabled 卡必须**有** thumb」(单测断言数据非空),
 *   **不是**「必须大面积展示」。改展示方式不动铁律。
 *
 * ═══ 三层文案的分工 ═══════════════════════════════════════════════════════════
 *   标题     = 产物名(产品术语,hub/侧栏/打标三处同名,不能乱改)
 *   一句话用途 = ★用户能拿它干什么 —— **视觉权重压过描述**,它才是帮用户做决定的那行
 *   描述     = 功能罗列,降为第二行小字
 *
 * ═══ z 轴纪律 ═════════════════════════════════════════════════════════════════
 *   z-40 推荐/即将徽标 · z-30 文案区+底栏 · z-20 hover 真样图 · z-10 人物 · z-0 舞台底
 *   ★文案区必须高于人物,否则「立即创作」被盖住(踩过)。人物虽然被 overflow 关在舞台区里,
 *   仍显式给层级 —— 靠"应该不会溢出"防事故是侥幸。
 *
 * ★ 编号 01-06 已去掉:taste-skill §9.F 明令禁 section-number eyebrows;卡片是**并列入口**
 *   不是有序步骤,编号不承载信息。
 */
import type { KeyboardEvent } from "react";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { ArrowRight } from "lucide-react";

import { useActiveSkin } from "@/shared/hooks/useTheme";
import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { figureOf } from "../model/figures";
import { SHAPE_META } from "../model/shapes";
import type { SceneCard } from "../model/types";
import { SceneShapeGlyph } from "./SceneShapeGlyph";

interface Props {
  card: SceneCard;
  /** enabled 卡点击(路由 + preset 由页面层做);soon/disabled 卡不传。 */
  onOpen?: () => void;
  /** 「最常用」推荐描边(终稿 .cap.rec)。 */
  recommended?: boolean;
  /** 入场 stagger 序号(R5 动效用;CSS 级联,不用 JS 监听滚动)。 */
  index?: number;
}

export function SceneCapCard({ card, onOpen, recommended, index = 0 }: Props) {
  const { book } = usePriceBook();
  const costLabel = card.costLabel(book);
  const clickable = card.enabled && !!onOpen && !card.soon;
  // 真源 = <html data-skin>(不是 localStorage);MutationObserver 跟随,切皮肤自动换人
  const skin = useActiveSkin();
  const shape = SHAPE_META[card.shape];

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.();
    }
  };

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-disabled={clickable ? undefined : true}
      onClick={clickable ? onOpen : undefined}
      onKeyDown={clickable ? onKeyDown : undefined}
      data-scene-card={card.key}
      data-scene-shape={card.shape}
      style={{ ["--scene-stagger" as string]: `${Math.min(index, 11) * 40}ms` }}
      className={cn(
        "scene-cap group relative flex flex-col overflow-hidden border bg-card text-left",
        // 形状锁单源(G1):原来是 `calc(--radius + 4px)`,与作品卡的 `* 1.5` 只在 standard
        // 皮肤下巧合相等(都 12px),cyber 16 vs 18 / comic 24 vs 30 是裂的。
        CARD_WALL_RADIUS,
        clickable ? "cursor-pointer" : "opacity-75",
        recommended && "border-primary shadow-sm"
      )}
    >
      {(recommended || card.soon) && (
        <span
          className={cn(
            "absolute right-2 top-2 z-40 rounded-full px-2 py-0.5 text-2xs font-semibold",
            recommended ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          )}
        >
          {recommended ? "最常用" : "即将"}
        </span>
      )}

      {/* ── 舞台区:人物(场景已烘进图里)+ 形制图示 + hover 真样图 ───────────── */}
      <div className="scene-stage relative w-full overflow-hidden bg-card">
        {card.figurePending ? (
          /* 主角图未产:显式占位(渐变 + 目的图标 + 橙标),不留空舞台假装有图。
             ★橙标是给我们自己看的施工牌,不是给用户的功能说明 —— 所以只标"待补",
             不写"敬请期待":卡是**真能点**的,能力不缺,缺的是那张自产素材。 */
          <div
            className="scene-fig absolute inset-0 z-10 grid place-items-center"
            style={{
              backgroundImage:
                "radial-gradient(120% 90% at 50% 15%, hsl(var(--primary)/0.22), hsl(var(--primary)/0.08) 55%, transparent)",
            }}
            aria-hidden
          >
            <card.figurePending.Icon className="h-12 w-12 text-primary/80" strokeWidth={1.5} />
            {/* ★橙标用**实底 warning + warning-foreground**,不用设计稿那版 `warning/.18` 淡底 +
                `text-warning` 文字:设计稿给三个皮肤各写了一份 `--warning`(standard 更偏橙),
                而真仓 `--warning` 只有一个全局值(42 100% 60%),淡底那版在 standard / comic
                的浅背景上落到 ~1.8:1,读不出来。实底那对 token 本来就是配套定义的(≈10:1),
                三皮肤都成立,且视觉上更像"橙标"。 */}
            {/* ★靠**左**上不靠右上:右上是「最常用 / 即将」徽标的位子(z-40,在卡根上),
                两个都贴 right-2 top-2 时会叠成一团乱码 —— 实测「产品带货」那张(既是最常用
                又缺主角图)就是这样。左上是空的:真产物 peek 的「真实产物」标签也在左上,
                但占位卡恒无 thumb(catalog.test 锁),两者不可能同时出现。 */}
            <span className="absolute left-2 top-2 rounded-md bg-warning px-1.5 py-0.5 text-2xs font-semibold text-warning-foreground">
              主角图待补
            </span>
          </div>
        ) : (
          /* 缺图降级:用 background-image 不用 <img>+onError —— 后者 404 时会先闪一次 alt */
          <div
            className="scene-fig absolute inset-0 z-10 bg-cover bg-[position:center_14%] bg-no-repeat"
            style={{ backgroundImage: `url(${figureOf(skin, card.key)})` }}
            aria-hidden
          />
        )}

        {/* hover 浮出真样图 —— 常驻不占面积,想看真货一 hover 就有 */}
        {card.thumb && (
          <div className="scene-peek absolute inset-x-0 bottom-0 z-20 h-[78%] overflow-hidden">
            <img
              src={card.thumb}
              alt={`${card.title}真实产物样图`}
              loading="lazy"
              className="h-full w-full object-cover object-top"
            />
            <span className="absolute left-2 top-1.5 rounded-full bg-card/85 px-1.5 py-0.5 text-2xs font-medium text-card-foreground backdrop-blur-sm">
              真实产物
            </span>
          </div>
        )}

        {/* 产物形制图示:样图裁 4:3 永远表达不了的那一维 */}
        <span className="scene-shape absolute bottom-2 left-2 z-30 inline-flex items-center gap-1.5 rounded-full bg-card/85 px-2 py-1 text-card-foreground backdrop-blur-sm">
          <SceneShapeGlyph shape={card.shape} />
          <span className="text-2xs font-medium leading-none">{shape.label}</span>
        </span>
      </div>

      {/* ── 文案区 ──────────────────────────────────────────────────────────── */}
      <div className="relative z-30 flex flex-1 flex-col gap-1 p-3">
        {/* h3:页面是 H1 页名 → H2 分区名,卡名必须接 H3,否则读屏用户无法按标题在卡墙里跳转 */}
        <h3 className="text-sm font-[650] tracking-[-0.012em]">{card.title}</h3>
        {/* ★ 一句话用途:字重与颜色都压过描述,眼睛先落这行 */}
        {/* 2026-08-26:描述抬底 11px→12px 后与用途(原 text-xs=12px)同号,靠字号拉开的那层塌了。
            用途提到 text-sm(14px)把差值补回来 —— 它本来就是卡面上最该被看清的一行。 */}
        <div className="text-sm font-medium leading-snug text-foreground/[0.86]">{card.usage}</div>
        {/* 空 desc ⇒ 整行不渲染(2026-09-12):视频 8 目的卡的卡面形制是「副题 + 叙事弧 chip」
            两层,没有第三行说明(见 catalog.ts PURPOSE_CARDS 头注)。留一个空 <p> 会在
            chip 行上方压出一道说不清来历的空隙 —— 其余每张卡 desc 都非空,渲染不受影响。 */}
        {card.desc && (
          <p className="line-clamp-2 text-2xs leading-[1.62] text-muted-foreground">{card.desc}</p>
        )}
        <div className="mt-auto flex flex-wrap gap-1 pt-1.5">
          {card.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-border/60 bg-background/50 px-1.5 py-0.5 text-2xs text-muted-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* ── 底栏:积分标 + 立即创作 ────────────────────────────────────────
          ★ 积分数字来自**后端价目表**(`usePriceBook` → `/pricing/catalog`),不再是前端常量。
            价目表没到货时 `costLabel` 返空串 → 这里渲染一条骨架,**不显示任何数字**:
            显示一个可能过期的价钱比显示「加载中」坏得多,因为用户会照着它做决定。 */}
      <div className="relative z-30 flex items-center justify-between border-t border-border/60 px-3 py-2">
        {costLabel ? (
          <span className="text-2xs text-muted-foreground">{costLabel}</span>
        ) : (
          <span
            className="h-3 w-24 animate-pulse rounded bg-muted/60"
            aria-label="积分标加载中"
          />
        )}
        {clickable && (
          <span className="inline-flex items-center gap-0.5 text-2xs font-semibold text-primary">
            立即创作
            <ArrowRight className="scene-cta-arrow h-3 w-3" />
          </span>
        )}
      </div>
    </div>
  );
}
